import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { Flame, Snowflake } from 'lucide-react';
import { LotteryBallGroup } from '../LotteryBall';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const COLORS = ['#22c55e', '#3b82f6', '#ec4899', '#f59e0b', '#06b6d4', '#8b5cf6'];

const TOOLTIP_STYLE = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '8px',
};

export const FrequencyTabContent = ({ frequencyData, distributionData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <Card className="glass-card" data-testid="frequency-chart-card">
      <CardHeader><CardTitle className="text-white">Top 20 Numeros Mas Frecuentes</CardTitle></CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={frequencyData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis type="number" tick={{ fill: '#94a3b8' }} />
              <YAxis dataKey="number" type="category" tick={{ fill: '#94a3b8', fontFamily: 'JetBrains Mono' }} tickFormatter={(val) => String(val).padStart(2, '0')} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#fff' }} />
              <Bar dataKey="count" fill="#22c55e" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
    <Card className="glass-card" data-testid="distribution-chart-card">
      <CardHeader><CardTitle className="text-white">Distribucion por Rangos</CardTitle></CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={distributionData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={100} dataKey="value">
                {distributionData.map((entry, index) => (
                  <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const HotColdTabContent = ({ analysis }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="glass-card border-pink-500/30" data-testid="hot-numbers-analysis">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Flame className="text-pink-400" />Numeros Calientes</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm mb-6">Numeros con mayor frecuencia en el periodo seleccionado</p>
          <div className="space-y-4">
            {analysis?.hot_numbers?.map((item, idx) => (
              <div key={`hot-${item.number}-${idx}`} className="flex items-center gap-4">
                <div className="lottery-ball-hot w-14 h-14">
                  <span className="font-mono font-bold text-white text-lg">{String(item.number).padStart(2, '0')}</span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-white font-medium">#{idx + 1}</span>
                    <span className="text-slate-400 font-mono">{item.count} apariciones</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2">
                    <div className="bg-pink-500 h-2 rounded-full" style={{ width: `${item.percentage * 3}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="glass-card border-cyan-500/30" data-testid="cold-numbers-analysis">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Snowflake className="text-cyan-400" />Numeros Frios</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm mb-6">Numeros con menor frecuencia en el periodo seleccionado</p>
          <div className="space-y-4">
            {analysis?.cold_numbers?.map((item, idx) => (
              <div key={`cold-${item.number}-${idx}`} className="flex items-center gap-4">
                <div className="lottery-ball-cold w-14 h-14">
                  <span className="font-mono font-bold text-white text-lg">{String(item.number).padStart(2, '0')}</span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-white font-medium">#{idx + 1}</span>
                    <span className="text-slate-400 font-mono">{item.count} apariciones</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2">
                    <div className="bg-cyan-500 h-2 rounded-full" style={{ width: `${item.percentage * 3}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    <Card className="glass-card" data-testid="overdue-numbers-card">
      <CardHeader><CardTitle className="text-white">Numeros Rezagados</CardTitle></CardHeader>
      <CardContent>
        <p className="text-slate-400 text-sm mb-4">Numeros que no han aparecido en mas de 20 sorteos</p>
        <div className="flex flex-wrap gap-4">
          {analysis?.overdue_numbers?.map((item, idx) => (
            <div key={`ov-${item.number}-${idx}`} className="flex flex-col items-center gap-1">
              <div className="lottery-ball w-12 h-12 text-sm border-amber-500/50">
                <span className="font-mono font-bold text-white">{String(item.number).padStart(2, '0')}</span>
              </div>
              <span className="text-amber-400 text-xs font-mono">{item.draws_ago} sorteos</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

const DAY_TRANSLATIONS = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miercoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sabado', sunday: 'Domingo',
};

export const TrendsTabContent = ({ trendData, analysis }) => (
  <div className="space-y-6">
    <Card className="glass-card" data-testid="trend-chart-card">
      <CardHeader><CardTitle className="text-white">Tendencia de Promedios (Ultimos 30 sorteos)</CardTitle></CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" tick={{ fill: '#94a3b8' }} />
              <YAxis tick={{ fill: '#94a3b8' }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#fff' }} />
              <Line type="monotone" dataKey="avg" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
    <Card className="glass-card" data-testid="day-stats-card">
      <CardHeader><CardTitle className="text-white">Numeros Mas Frecuentes por Dia</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {analysis?.trends?.by_day && Object.entries(analysis.trends.by_day).map(([day, numbers]) => (
            <div key={day} className="p-4 bg-slate-800/50 rounded-lg">
              <p className="text-slate-400 text-sm capitalize mb-3">{DAY_TRANSLATIONS[day] || day}</p>
              <div className="flex flex-wrap gap-2">
                {numbers.slice(0, 3).map((n, idx) => (
                  <div key={`rb-${n.number}-${idx}`} className="lottery-ball w-10 h-10 text-xs">
                    <span className="font-mono font-bold text-white">{String(n.number).padStart(2, '0')}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

export const PairsTabContent = ({ analysis }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <Card className="glass-card" data-testid="pairs-card">
      <CardHeader><CardTitle className="text-white">Pares Mas Frecuentes</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {analysis?.pairs?.slice(0, 10).map((pair) => (
          <div key={`pair-${pair.pair?.[0]}-${pair.pair?.[1]}`} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <div className="flex items-center gap-2">
              <LotteryBallGroup numbers={pair.pair} size="sm" />
            </div>
            <span className="text-green-400 font-mono font-bold">{pair.count}x</span>
          </div>
        ))}
      </CardContent>
    </Card>
    <Card className="glass-card" data-testid="triplets-card">
      <CardHeader><CardTitle className="text-white">Tripletas Mas Frecuentes</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {analysis?.triplets?.slice(0, 10).map((triplet) => (
          <div key={`trip-${triplet.triplet?.join('-')}`} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <div className="flex items-center gap-2">
              <LotteryBallGroup numbers={triplet.triplet} size="sm" />
            </div>
            <span className="text-blue-400 font-mono font-bold">{triplet.count}x</span>
          </div>
        ))}
      </CardContent>
    </Card>
  </div>
);
